import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';

const UAD_SOURCE = 'https://w3c.github.io/webappsec-privacy-considerations/';

/**
 * User-Agent Client Hints.
 *
 * The high-entropy values (model, bitness, platformVersion, fullVersionList)
 * are available on Chromium and are a strong coherence target: they are harder
 * to spoof consistently than the UA string, and a mismatch with navigator.*
 * is meaningful evidence of automation or partial spoofing.
 */
export const clientHintsProbe: Probe = {
  id: 'clientHints',
  label: 'User-Agent Client Hints',
  layer: 'browser',
  tier: 'client',
  category: 'browser',
  description: 'navigator.userAgentData brands and high-entropy hints',
  budgetMs: 2000,

  async run(): Promise<ProbeResult> {
    const uad = (navigator as Navigator & { userAgentData?: NavigatorUADataLike }).userAgentData;
    const findings: Finding[] = [];

    if (!uad) {
      return {
        signals: {
          supported: na<boolean>('unsupported', 'client', 'navigator.userAgentData absent (non-Chromium or disabled)'),
          brands: na<unknown[]>('unsupported', 'client'),
        },
      };
    }

    const brands = uad.brands?.map((b) => ({ brand: b.brand, version: b.version })) ?? [];
    const mobile = uad.mobile ?? null;
    const platform = uad.platform ?? null;

    // Request the high-entropy values. This can reject on hardened browsers.
    let high: Record<string, unknown> = {};
    let highStatus: 'ok' | 'denied' | 'error' = 'ok';
    try {
      if (uad.getHighEntropyValues) {
        high = (await uad.getHighEntropyValues([
          'architecture',
          'bitness',
          'model',
          'platformVersion',
          'uaFullVersion',
          'fullVersionList',
          'wow64',
        ])) as Record<string, unknown>;
      }
    } catch (err) {
      highStatus = 'error';
      findings.push({
        id: 'clienthints.getHighEntropyValues-failed',
        severity: 'low',
        category: 'browser',
        layer: 'browser',
        confidence: 'low',
        title: 'getHighEntropyValues() failed',
        detail:
          'Requesting high-entropy client hints rejected. Can happen in hardened/privacy browsers, so it is weak evidence on its own.',
        sources: [UAD_SOURCE],
        evidence: [String(err)],
        scoreContribution: 0,
      });
    }

    const platformVersion = (high.platformVersion as string) ?? null;

    // Coherence: UA-CH platform should agree with the UA string's platform token.
    const ua = navigator.userAgent;
    const uaSaysWindows = /Windows NT/.test(ua);
    const uaSaysMac = /Macintosh|Mac OS X/.test(ua);
    const chSaysWindows = platform === 'Windows';
    const chSaysMac = platform === 'macOS';
    const platformMismatch = (uaSaysWindows && !chSaysWindows && chSaysMac) || (uaSaysMac && chSaysMac === false && platform === 'Windows');
    if (platformMismatch) {
      findings.push({
        id: 'clienthints.platform-mismatch',
        severity: 'medium',
        category: 'browser',
        layer: 'browser',
        confidence: 'medium',
        title: 'Client Hints platform disagrees with the User-Agent string',
        detail:
          'The Sec-CH-UA-Platform hint and the User-Agent describe different operating systems. Genuine browsers keep these in sync; a mismatch usually means one was patched.',
        sources: [UAD_SOURCE],
        evidence: [`UA=${ua}`, `Sec-CH-UA-Platform=${platform}`],
        scoreContribution: 20,
      });
    }

    return {
      signals: {
        supported: ok(true),
        brands: ok(brands),
        mobile: ok(mobile),
        platform: ok(platform),
        architecture: ok((high.architecture as string) ?? null),
        bitness: ok((high.bitness as string) ?? null),
        model: ok((high.model as string) ?? null),
        platformVersion: ok(platformVersion),
        uaFullVersion: ok((high.uaFullVersion as string) ?? null),
        fullVersionList: ok((high.fullVersionList as unknown) ?? null),
        wow64: ok((high.wow64 as boolean) ?? null),
        highEntropyStatus: ok(highStatus, 'client'),
      },
      findings,
      parts: {
        deviceInfo: {
          uaPlatform: platform,
          uaMobile: mobile,
          uaArchitecture: (high.architecture as string) ?? null,
          uaBitness: (high.bitness as string) ?? null,
        },
      },
    };
  },
};

interface NavigatorUADataLike {
  brands?: Array<{ brand: string; version: string }>;
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
}

registerProbe(clientHintsProbe);
